import { Modal as BSModal } from 'react-bootstrap';
import { NewIconButton, Typography } from 'app/components';
import { i18n } from 'app/utils/i18n';
import { AffectedPage, AffectedTag } from './ContentViews/ListItemsTable';

import './FindingDetailsModal.scss';

export interface FindingDetails {
  title: string;
  type: string;
  status: string;
  message: string;
  code: string;
  affectedCount?: number;
  affectedPages?: AffectedPage[];
  affectedTags?: AffectedTag[];
}

interface FindingDetailsModalProps {
  show: boolean;
  finding: FindingDetails | null;
  onClose: () => void;
  onNavigateToPage?: (page: AffectedPage) => void;
  onNavigateToTag?: (tag: AffectedTag) => void;
}

function pageLabel(page: AffectedPage): string {
  const base = page.pageNumber == null ? page.sectionName || i18n('all.tags.page') : `${i18n('all.tags.page')} ${page.pageNumber}`;
  return page.count && page.count > 1 ? `${base} (×${page.count})` : base;
}

function tagLocationLabel(tag: AffectedTag): string {
  const page = tag.pageNumber != null ? `${i18n('all.tags.page')} ${tag.pageNumber}` : null;
  if (page && tag.sectionName) return `${page} · ${tag.sectionName}`;
  return page ?? tag.sectionName ?? i18n('all.tags.page');
}

function tagValuePreview(tag: AffectedTag): string | null {
  const d = tag.details;
  if (!d) return null;
  const value = d.value && d.value !== 'None' ? d.value : null;
  const html = d['html value'] && d['html value'] !== value ? d['html value'] : null;
  if (!value && !html) return null;
  if (value && html) return `${value}  ·  ${html}`;
  return value ?? html;
}

export const FindingDetailsModal = ({ show, finding, onClose, onNavigateToPage, onNavigateToTag }: FindingDetailsModalProps) => {
  return (
    <BSModal show={show} onHide={onClose} centered size="xl" contentClassName="finding-details-modal" scrollable>
      <BSModal.Body className="finding-details-modal__content">
        <div className="finding-details-modal__header">
          <Typography variant="body-8" fontWeight="semibold">
            {finding?.title}
          </Typography>
          <NewIconButton icon="close" variant="transparent" onClick={onClose} className="finding-details-modal__close-button" />
        </div>

        {finding && (
          <div className="finding-details-modal__body">
            <div className="finding-details-modal__meta">
              <span className={`finding-details-modal__status finding-details-modal__status--${finding.status}`}>{i18n(`tag.status.${finding.status}`)}</span>
              <Typography variant="body-5" color="#6B7076">
                {finding.type}
              </Typography>
              {finding.affectedCount && finding.affectedCount > 1 && (
                <Typography variant="body-5" color="#6B7076">
                  {`${finding.affectedCount} taggar`}
                </Typography>
              )}
              {/* Page-level chips kept as a summary when we have no per-tag breakdown */}
              {(!finding.affectedTags || finding.affectedTags.length === 0) && finding.affectedPages && finding.affectedPages.length > 0 && (
                <div className="finding-details-modal__pages">
                  {finding.affectedPages.map((page) => (
                    <button
                      key={page.pageId}
                      type="button"
                      className="finding-details-modal__page-chip"
                      onClick={() => onNavigateToPage?.(page)}
                      title={page.sectionName}
                    >
                      {pageLabel(page)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Typography variant="body-5">{finding.message}</Typography>

            {finding.affectedTags && finding.affectedTags.length > 0 && (
              <div className="finding-details-modal__tags">
                <Typography variant="body-3" fontWeight="semibold" color="#547BA2" className="caps">
                  {i18n('tag.validation.affected.tags')}
                </Typography>
                <ul className="finding-details-modal__tag-list">
                  {finding.affectedTags.map((tag) => {
                    const preview = tagValuePreview(tag);
                    return (
                      <li key={tag.tagId} className="finding-details-modal__tag-row">
                        <button type="button" className="finding-details-modal__tag-button" onClick={() => onNavigateToTag?.(tag)} title={tag.sectionName}>
                          <span className="finding-details-modal__tag-text">
                            <span className="finding-details-modal__tag-concept">{tag.conceptQName}</span>
                            {preview && <span className="finding-details-modal__tag-preview">{preview}</span>}
                          </span>
                          <span className="finding-details-modal__tag-location">{tagLocationLabel(tag)}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <Typography variant="body-3" color="#9CA3AF" className="finding-details-modal__code">
              {finding.code}
            </Typography>
          </div>
        )}
      </BSModal.Body>
    </BSModal>
  );
};
